import {
  BridgeSwapQuote,
  isBridgeSwapQuote,
  SwapQuote,
} from '~/components/forms/swap/hooks/useSwap'

// The leg whose provider settles the swap asynchronously. A composed quote
// mirrors the settling step's providerTransactionId at the top level but keeps
// its own routerType, so the step is the source of truth when steps exist.
export const getSettlement = (
  swapQuote: SwapQuote,
): BridgeSwapQuote | undefined =>
  (swapQuote.steps ?? []).find(isBridgeSwapQuote) ??
  (isBridgeSwapQuote(swapQuote) ? swapQuote : undefined)

export const percentageToBps = (percentage: number) => percentage * 100
export const bpsToPercentage = (bps: number) => bps / 100

const divCeil = (a: bigint, b: bigint) => (a + b - 1n) / b

const reduceBySlippage = (amount: bigint, slippage: number) => {
  return (amount * (10_000n - BigInt(slippage))) / 10_000n
}

const increaseBySlippage = (amount: bigint, slippage: number) => {
  return divCeil(amount * (10_000n + BigInt(slippage)), 10_000n)
}

export const getSlippageAdjustedAmounts = (
  amount: bigint,
  slippage: number,
) => ({
  minimumReceived: reduceBySlippage(amount, slippage),
  maximumAmountSpent: increaseBySlippage(amount, slippage),
})

/**
 * SLIPPAGE CONSTANTS
 */
const STABLECOIN_SLIPPAGE_BPS = 10 // 0.1% for stablecoins
const COMMON_STABLES = [
  'USDC',
  'USDC.E',
  'USDT',
  'USDT0',
  'DAI',
  'EURC',
  'EURC.E',
]

const MAJOR_ASSETS_SLIPPAGE_BPS = 50 // 0.5% for major assets
const MAJOR_ASSETS = [
  'ETH',
  'WETH',
  'STETH',
  'WSTETH',
  'BTC',
  'WBTC',
  'CBBTC',
  'BNB',
  'MATIC',
  'AVAX',
  'OP',
  'ARB',
  'LUSD',
  'FRAX',
  'PYUSD',
  'USDP',
  'TUSD',
  'GUSD',
  'CRVUSD',
  'GUSDC',
]

/**
 * Returns the default slippage for a swap based on common token constants
 *
 * We use the token symbol for easiest check across chains.
 * If the token doesn't have a symbol or one can't be found, we use the max default
 */
export const DEFAULT_SLIPPAGE_BPS = 150 // Worst-case slippage
export const getDefaultSlippageBps = (
  inputTokenSymbol: string | undefined,
  outputTokenSymbol: string | undefined,
) => {
  if (inputTokenSymbol === undefined || outputTokenSymbol === undefined) {
    return DEFAULT_SLIPPAGE_BPS
  }

  const inputIsStable = COMMON_STABLES.includes(inputTokenSymbol.toUpperCase())
  const outputIsStable = COMMON_STABLES.includes(
    outputTokenSymbol.toUpperCase(),
  )

  const inputIsMajorAsset = MAJOR_ASSETS.includes(
    inputTokenSymbol.toUpperCase(),
  )
  const outputIsMajorAsset = MAJOR_ASSETS.includes(
    outputTokenSymbol.toUpperCase(),
  )

  if (inputIsStable && outputIsStable) {
    return STABLECOIN_SLIPPAGE_BPS
  }

  if (
    (inputIsStable || inputIsMajorAsset) &&
    (outputIsStable || outputIsMajorAsset)
  ) {
    return MAJOR_ASSETS_SLIPPAGE_BPS
  }

  return DEFAULT_SLIPPAGE_BPS
}
